import { useCallback, useEffect, useRef, useState } from 'react';

import useAxios from 'axios-hooks';
import { dequal } from 'dequal/lite';

import { useSports } from 'contexts/SportContext';
import { ColumnIds } from 'enums';
import { getSimplifiedTrackingStatusNameByDescription } from 'features/proposal/components/SimplifiedProposal/IngestionProposal.utils';
import { usePaginationQueryParams } from 'hooks/common/usePaginationQueryParams';
import { setPaginationState } from 'utils/local.storage.utils';
import { buildRequestParams } from 'utils/pagination.utils';

export interface usePaginatedRequestProps {
  endpoint: string;
  initialParams?: BasicPaginationProps;
  staticFilters?: Array<PaginationFilter>;
  staticSortBy?: Array<PaginationSort>;
  disableSportId?: boolean;
  storageKey?: string;
  manual?: boolean;
  disablePaginatedQueryParams?: boolean;
}

export interface PaginatedPayload<T> {
  pageSize: number;
  page: number;
  totalItems: number;
  items: Array<T>;
}

export function usePaginatedRequest<T = any>({
  endpoint,
  initialParams,
  storageKey,
  staticFilters,
  staticSortBy,
  disableSportId,
  manual,
  disablePaginatedQueryParams = false,
}: usePaginatedRequestProps) {
  const { sportId } = useSports();

  //THIS IS ONLY FOR INGESTION
  const isIngestion = window.location.pathname.includes('/ingestion');
  //THIS IS ONLY FOR INGESTION

  // Initialice states from initial params (states are not recomputed when initialParams change)
  const { page, pageSize, sortBy, filters } = initialParams || {};
  const [currentPage, setCurrentPage] = useState<number | null>(page || 1);
  const [currentPageSize, setCurrentPageSize] = useState<number | null>(pageSize || 20);
  const skipNextInitialParamsSyncRef = useRef(false);
  const [currentFilters, setCurrentFilters] = useState<PaginationFilter[]>(
    sportId && !disableSportId
      ? [{ columnId: ColumnIds.SportId, value: sportId }, ...(filters || [])]
      : filters || [],
  );
  const [currentSortBy, setCurrentSortBy] = useState<PaginationSort[]>(sortBy || []);

  const normalizeIngestionFilters = useCallback(
    (value: Array<PaginationFilter>) =>
      (value || []).map(filter => {
        if (isIngestion && filter?.columnId === ColumnIds.Status) {
          const statusNames = filter.value
            .toString()
            .split(',')
            .map(status => getSimplifiedTrackingStatusNameByDescription(status.trim()))
            .join(',');

          return {
            ...filter,
            value: statusNames,
          };
        }

        return filter;
      }),
    [isIngestion],
  );

  const params = buildRequestParams({
    page: currentPage,
    pageSize: currentPageSize,
    filters: normalizeIngestionFilters(
      staticFilters ? [...currentFilters, ...staticFilters] : currentFilters,
    ),
    sortBy: staticSortBy ? [...currentSortBy, ...staticSortBy] : currentSortBy,
  });

  const [{ data, loading, error }, refetch] = useAxios<PaginatedPayload<T>>(
    {
      url: endpoint,
      params,
    },
    manual && { manual },
  );

  const { handlePaginationQueryParamsChange } = usePaginationQueryParams();

  const lastSyncedInitialParamsRef = useRef<{
    sportId: number | undefined;
    disableSportId: boolean | undefined;
    page: number;
    pageSize: number;
    filters: PaginationFilter[];
    sortBy: PaginationSort[];
  } | null>(null);

  useEffect(() => {
    if (skipNextInitialParamsSyncRef.current) {
      skipNextInitialParamsSyncRef.current = false;
      return;
    }

    // Reset the filters to the initial ones when sportId changes
    // Everything must be updated in a single render so useAxios will run once
    setCurrentPage(page || 1);
    setCurrentPageSize(pageSize || 20);
    setCurrentFilters(
      sportId && !disableSportId
        ? [{ columnId: ColumnIds.SportId, value: sportId }, ...(filters || [])]
        : filters || [],
    );
    const nextPage = page || 1;
    const nextPageSize = pageSize || 20;
    const nextFilters =
      sportId && !disableSportId
        ? [{ columnId: ColumnIds.SportId, value: sportId }, ...(filters || [])]
        : filters || [];
    const nextSortBy = sortBy || [];

    const snapshot = {
      sportId,
      disableSportId,
      page: nextPage,
      pageSize: nextPageSize,
      filters: nextFilters,
      sortBy: nextSortBy,
    };

    if (
      lastSyncedInitialParamsRef.current !== null &&
      dequal(lastSyncedInitialParamsRef.current, snapshot)
    ) {
      return;
    }

    lastSyncedInitialParamsRef.current = snapshot;

    setCurrentPage(nextPage);
    setCurrentPageSize(nextPageSize);
    setCurrentFilters(nextFilters);
    setCurrentSortBy(nextSortBy);
  }, [sportId, disableSportId, page, pageSize, filters, sortBy]);

  useEffect(() => {
    if (storageKey) {
      setPaginationState(storageKey, {
        // SportId storing/restoration is pending to discuss
        filters: currentFilters.filter(f => f.columnId !== ColumnIds.SportId),
        sortBy: currentSortBy,
        page: currentPage,
        pageSize: currentPageSize,
      });
    }
  }, [currentFilters, storageKey, currentSortBy, currentPage, currentPageSize]);

  const setPage = useCallback(
    (page: number) => {
      setCurrentPage(page);

      if (storageKey && !disablePaginatedQueryParams) {
        handlePaginationQueryParamsChange({ page });
      }
    },
    [disablePaginatedQueryParams, handlePaginationQueryParamsChange, storageKey],
  );

  const setPageSize = useCallback(
    (value: number) => {
      setCurrentPage(1);
      setCurrentPageSize(value);

      if (storageKey && !disablePaginatedQueryParams) {
        handlePaginationQueryParamsChange({ page: 1, pageSize: value });
      }
    },
    [disablePaginatedQueryParams, handlePaginationQueryParamsChange, storageKey],
  );

  const setFilters = useCallback(
    (value: Array<PaginationFilter>) => {
      skipNextInitialParamsSyncRef.current = true;
      const updatedFilters = normalizeIngestionFilters(value);

      setCurrentFilters(
        sportId && !disableSportId
          ? [{ columnId: ColumnIds.SportId, value: sportId }, ...updatedFilters]
          : updatedFilters,
      );
      setCurrentPage(1);

      if (storageKey && !disablePaginatedQueryParams) {
        handlePaginationQueryParamsChange({ page: 1, filters: updatedFilters });
      }
    },
    [
      disablePaginatedQueryParams,
      disableSportId,
      handlePaginationQueryParamsChange,
      normalizeIngestionFilters,
      sportId,
      storageKey,
    ],
  );

  const setSortBy = useCallback(
    (value: Array<PaginationSort>) => {
      skipNextInitialParamsSyncRef.current = true;
      setCurrentPage(1);
      setCurrentSortBy(value);
      if (storageKey && !disablePaginatedQueryParams) {
        handlePaginationQueryParamsChange({ page: 1, sortBy: value });
      }
    },
    [disablePaginatedQueryParams, handlePaginationQueryParamsChange, storageKey],
  );

  const setFiltersAndSortBy = useCallback(
    (filtersValue: Array<PaginationFilter>, sortByValue: Array<PaginationSort>) => {
      skipNextInitialParamsSyncRef.current = true;
      const updatedFilters = normalizeIngestionFilters(filtersValue);

      setCurrentPage(1);
      setCurrentFilters(
        sportId && !disableSportId
          ? [{ columnId: ColumnIds.SportId, value: sportId }, ...updatedFilters]
          : updatedFilters,
      );
      setCurrentSortBy(sortByValue || []);

      if (storageKey && !disablePaginatedQueryParams) {
        handlePaginationQueryParamsChange({
          page: 1,
          filters: updatedFilters,
          sortBy: sortByValue || [],
        });
      }
    },
    [
      disablePaginatedQueryParams,
      disableSportId,
      handlePaginationQueryParamsChange,
      normalizeIngestionFilters,
      sportId,
      storageKey,
    ],
  );

  return {
    data: data || ({} as PaginatedPayload<T>),
    refetch,
    isLoading: loading,
    error,
    setPage,
    setPageSize,
    setFilters,
    setSortBy,
    setFiltersAndSortBy,
  };
}
